import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { clientDispatch } from 'src/redux/clientStore';
import { fetchProfitPercent } from 'src/redux/slices/client-profit-slice';
import { contractBalanceUpdated } from 'src/redux/slices/contract-balance-slice';
import { axiosInstanceNew } from 'src/utils/axios';
import { formatAmount } from 'src/utils/formatNumber';

export function LiquidityBar() {
  const theme = useTheme();

  const balance = useSelector((state) => state.contractBalance);
  const priceETH = useSelector((state) => state?.prices?.byId?.ethereum?.usd) || 0;

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      try {
        const [balanceRes] = await Promise.all([
          axiosInstanceNew.get('/tokens/contract-balance'),
          clientDispatch(fetchProfitPercent()),
        ]);

        if (ignore) return;

        if (balanceRes?.data) {
          const { eth, usdt, usd } = balanceRes.data;
          clientDispatch(contractBalanceUpdated({ eth, usdt, usd }));
        }
      } catch (err) {
        console.error('Failed to fetch initial swap data:', err);
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, []);

  // The slice initialises eth/usdt/usd as null and the reducer only writes
  // real values via ??, so null means "not yet received from the server".
  const isLoaded = balance?.usd !== null || balance?.usdt !== null || balance?.eth !== null;

  const usd = Math.max(Number(balance?.usd) || 0, 0);
  const usdt = Math.max(Number(balance?.usdt) || 0, 0);
  const eth = Math.max(Number(balance?.eth) || 0, 0);
  const ethUsdPrice = Math.max(Number(priceETH) || 0, 0);

  const usdValue = usd;
  const usdtValue = usdt;
  const ethValueUsd = eth * ethUsdPrice;

  const totalLiquidityUsd = usdValue + usdtValue + ethValueUsd;

  const usdPct = totalLiquidityUsd > 0 ? (usdValue / totalLiquidityUsd) * 100 : 0;
  const usdtPct = totalLiquidityUsd > 0 ? (usdtValue / totalLiquidityUsd) * 100 : 0;
  const ethPct = totalLiquidityUsd > 0 ? (ethValueUsd / totalLiquidityUsd) * 100 : 0;

  const isDark = theme.palette.mode === 'dark';

  // Solid, saturated segment colors. The bar IS the data, so it should be the
  // most vivid thing on screen — no alpha fade. Dark mode gets slightly
  // brighter tones to hold up against the dark track.
  const usdColor = isDark ? '#34d77a' : '#16a34a';
  const usdtColor = isDark ? '#fbbf24' : '#d97706';
  const ethColor = isDark ? '#60a5fa' : '#2563eb';

  const segments = [
    { key: 'usd', label: 'USD', amount: usd, pct: usdPct, color: usdColor },
    { key: 'usdt', label: 'USDT', amount: usdt, pct: usdtPct, color: usdtColor },
    { key: 'eth', label: 'ETH', amount: eth, pct: ethPct, color: ethColor },
  ];

  const trackColor = isDark
    ? alpha(theme.palette.common.white, 0.1)
    : alpha(theme.palette.common.black, 0.08);

  const dotSx = (color) => ({
    display: 'inline-block',
    width: 9,
    height: 9,
    mr: 0.65,
    borderRadius: '50%',
    bgcolor: color,
    flexShrink: 0,
    // ring keeps the dot visible if it lands on a same-hue background
    boxShadow: `0 0 0 1.5px ${alpha(color, 0.28)}`,
  });

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Progress bar ── */}
      {isLoaded ? (
        <Box
          sx={{
            width: '100%',
            height: 10,
            borderRadius: 999,
            overflow: 'hidden',
            display: 'flex',
            bgcolor: trackColor,
            border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.12 : 0.08)}`,
          }}
        >
          {segments.map((seg, i) => {
            // Tiny non-zero slices still get a sliver of width so they don't
            // vanish; zero stays zero.
            const width = seg.pct > 0 ? `${Math.max(seg.pct, 2)}%` : '0%';

            return (
              <Box
                key={seg.key}
                sx={{
                  width,
                  bgcolor: seg.color,
                  transition: 'width 320ms ease',
                  // hairline divider between adjacent segments so two colors
                  // never blur into one band
                  borderRight:
                    i < segments.length - 1 && seg.pct > 0
                      ? `1.5px solid ${isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.7)'}`
                      : 'none',
                }}
              />
            );
          })}
        </Box>
      ) : (
        <Skeleton variant="rounded" width="100%" height={10} sx={{ borderRadius: 999 }} />
      )}

      {/* ── Labels ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 1.75 },
          flexWrap: 'wrap',
          mt: 0.85,
          mb: 0.5,
        }}
      >
        {isLoaded ? (
          <>
            {segments.map((seg) => (
              <Box
                key={seg.key}
                sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
              >
                <Box component="span" sx={dotSx(seg.color)} />
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: { xs: 12, sm: 12.5 },
                    letterSpacing: 0.1,
                  }}
                >
                  {seg.label}:{' '}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    {formatAmount(seg.amount)}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, ml: 0.4 }}>
                    ({seg.pct.toFixed(0)}%)
                  </Box>
                </Typography>
              </Box>
            ))}
          </>
        ) : (
          <>
            <Skeleton variant="rounded" width={92} height={16} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={92} height={16} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={92} height={16} sx={{ borderRadius: 1 }} />
          </>
        )}

        <Tooltip
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={4000}
          title="This shows the funds currently available in SafiSwap to complete swaps and payouts. Liquidity can change as users deposit, swap, or withdraw."
        >
          <Box
            component="span"
            sx={{
              width: 18,
              height: 18,
              display: 'inline-grid',
              placeItems: 'center',
              borderRadius: '50%',
              fontSize: 12,
              fontWeight: 900,
              cursor: 'help',
              color: 'text.secondary',
              bgcolor: alpha(theme.palette.text.primary, 0.1),
              border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
            }}
          >
            i
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
